import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { FORM_MODE } from 'shared/enums';
import { IEPS } from 'interfaces/EPS';
import { IEPSState, INewEPS, IUpdateEPS } from './interfaces';
import { IMyKnownError, REQUEST_LIFE_CICLE } from 'interfaces/requests';
import { get, put, post, MICROSERVICES } from 'api';
import endpoints from './endpoints';
import {
  getErrorMessageAtCreate,
  getParsedEps,
  parseResponseEps,
} from './helpers';
import { openAlert } from 'modules/alertHandler';
import { setEpsModeAction } from '../epsNavigationMode';

export const loadEpsAction = createAsyncThunk<
  IEPS,
  string,
  { rejectValue: IMyKnownError }
>('eps/fetch', async (payload, { rejectWithValue }) => {
  try {
    const result = await get<IEPS>(MICROSERVICES.STRATEGIES, {
      url: endpoints.loadEps(),
      params: { _id: payload },
    });
    return parseResponseEps(result.data);
  } catch (error) {
    console.error('error at load eps', error);
    return rejectWithValue(error.message);
  }
});

export const registerEpsAction = createAsyncThunk<
  IEPS,
  INewEPS,
  { rejectValue: { message: string; current: IEPS } }
>(
  'eps/register',
  async ({ eps, setSubmitting }, { dispatch, rejectWithValue }) => {
    const parsedEPS = getParsedEps(eps, ['isActive']);
    try {
      const result = await post<IEPS>(MICROSERVICES.STRATEGIES, {
        url: endpoints.epsRegister(),
        data: parsedEPS,
      });
      setSubmitting(false);
      dispatch(
        openAlert({
          title: 'Sucesso!',
          content: 'Eps criada com sucesso',
        }),
      );
      dispatch(setEpsModeAction(FORM_MODE.list));
      return parseResponseEps(result.data);
    } catch (error) {
      console.error(error.response.data.message, error);
      setSubmitting(false);
      const errorMessage = getErrorMessageAtCreate(
        error.response.data.keyValue,
      );
      dispatch(openAlert(errorMessage));
      return rejectWithValue({
        message: error.response.data,
        current: eps,
      });
    }
  },
);

export const editEpsAction = createAsyncThunk<
  IEPS,
  IUpdateEPS,
  { rejectValue: { message: string; current: IEPS } }
>(
  'eps/edit',
  async ({ id, eps, setSubmitting }, { dispatch, rejectWithValue }) => {
    const parsedEPS = getParsedEps(eps);
    try {
      const result = await put<IEPS>(MICROSERVICES.STRATEGIES, {
        url: endpoints.epsUpdate(id),
        data: parsedEPS,
      });
      setSubmitting(false);
      dispatch(
        openAlert({
          title: 'Sucesso!',
          content: 'Eps editada com sucesso',
        }),
      );
      dispatch(setEpsModeAction(FORM_MODE.list));
      return parseResponseEps(result.data);
    } catch (error) {
      console.error(error.response.data.message, error);

      const isInactiveMessage =
        !parsedEPS.isActive &&
        'Antes de atualizar a EPS, a mesma precisa estar com o campo "Status" como "Ativa".';
      const isSameCodeSapOrContractNumberMessage =
        error.response.data.message.match(/codeSap|contractNumber/) &&
        'Em uma EPS reativada, os campos "Nº do contrato" e "Código do fornecedor no SAP", devem ser diferentes dos anteriores!';

      const errorMessage =
        isInactiveMessage ||
        isSameCodeSapOrContractNumberMessage ||
        'Erro para atualizar a EPS';

      setSubmitting(false);
      dispatch(
        openAlert({
          title: 'Erro!',
          content: errorMessage,
        }),
      );
      return rejectWithValue({ message: error.response.data, current: eps });
    }
  },
);

const initialState: IEPSState = {
  errorMsg: '',
  eps: {} as IEPS,
  loading: REQUEST_LIFE_CICLE.IDLE,
  submitting: REQUEST_LIFE_CICLE.IDLE,
  loadingByCNPJ: REQUEST_LIFE_CICLE.IDLE,
  foundSameCNPJ: false,
};

const epsSlice = createSlice({
  name: 'eps',
  initialState,
  reducers: {
    epsClear: () => initialState,
    setEps: (state, action: PayloadAction<IEPS>) => ({
      ...state,
      eps: action.payload,
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(loadEpsAction.fulfilled, (state, action) => {
      state.eps = action.payload;
      state.loading = REQUEST_LIFE_CICLE.SUCCEEDED;
    });
    builder.addCase(loadEpsAction.pending, (state) => {
      state.loading = REQUEST_LIFE_CICLE.PENDING;
    });
    builder.addCase(loadEpsAction.rejected, (state, action) => {
      state.errorMsg = action.payload?.message || '';
      state.loading = REQUEST_LIFE_CICLE.FAILED;
    });

    builder.addCase(registerEpsAction.fulfilled, (state, action) => {
      state.eps = action.payload;
      state.submitting = REQUEST_LIFE_CICLE.SUCCEEDED;
    });
    builder.addCase(registerEpsAction.pending, (state) => {
      state.submitting = REQUEST_LIFE_CICLE.PENDING;
    });
    builder.addCase(registerEpsAction.rejected, (state, action) => {
      state.errorMsg = action.payload?.message || '';
      state.submitting = REQUEST_LIFE_CICLE.FAILED;
      state.eps = action.payload?.current;
    });

    builder.addCase(editEpsAction.fulfilled, (state, action) => {
      state.eps = action.payload;
      state.submitting = REQUEST_LIFE_CICLE.SUCCEEDED;
    });
    builder.addCase(editEpsAction.pending, (state) => {
      state.submitting = REQUEST_LIFE_CICLE.PENDING;
    });
    builder.addCase(editEpsAction.rejected, (state, action) => {
      state.errorMsg = action.payload?.message || '';
      state.submitting = REQUEST_LIFE_CICLE.FAILED;
      state.eps = action.payload?.current;
    });
  },
});

export const { epsClear, setEps } = epsSlice.actions;
export default epsSlice.reducer;
